/**
 * Redirect Handler Utility
 * Handles post-authentication redirects based on URL parameters
 * Now with Firebase Remote Config support for dynamic configuration
 * Full case-insensitive support for both parameter names and values
 */

import { remoteConfig } from '../config/firebase';
import { fetchAndActivate, getValue } from 'firebase/remote-config';

// NOT 'redirectAfterLogin' — that one holds a full path for ProtectedRoute and
// the uptime deep links. Sharing it meant each scheme read the other's value
// and this one's 500ms clear wiped deep links. See ProtectedRoute.
const REDIRECT_STORAGE_KEY = 'redirectAfterLoginKey';
const REDIRECT_IN_PROGRESS_KEY = 'redirectInProgress';

/**
 * Cache for remote config values to avoid repeated fetches
 */
let configCache = null;

/**
 * Normalizes a string to lowercase for case-insensitive matching
 * @param {string} value - The value to normalize
 * @returns {string|null} - Normalized lowercase value or null
 */
const normalize = (value) => {
    if (!value || typeof value !== 'string') return null;
    return value.toLowerCase().trim();
};

/**
 * Fetches redirect configuration from Firebase Remote Config
 * @returns {Promise<{redirectMap: Object, defaultRedirect: string}>}
 */
export const fetchRedirectConfig = async () => {
    // Return cached config if available
    if (configCache) {
        return configCache;
    }

    try {
        // Activate the remote config
        await fetchAndActivate(remoteConfig);

        // Fetch redirect map from remote config
        const redirectMapValue = getValue(remoteConfig, 'redirect_map').asString();
        const redirectMapRaw = JSON.parse(redirectMapValue);

        // Normalize all keys in redirectMap to lowercase for case-insensitive lookup
        const redirectMap = {};
        Object.keys(redirectMapRaw).forEach(key => {
            redirectMap[normalize(key)] = redirectMapRaw[key];
        });

        // Fetch default redirect from remote config
        const defaultRedirect = "/dashboard/statistics";

        // Cache the config
        configCache = {
            redirectMap,
            defaultRedirect
        };

        // console.log('[RedirectHandler] Fetched redirect config from Remote Config:', configCache);

        return configCache;
    } catch (error) {
        console.error('[RedirectHandler] Error fetching remote config:', error);

        // Fallback to hardcoded values if remote config fails
        const fallbackConfig = {
            redirectMap: {
                'plan': '/dashboard/plans',
                'plans': '/dashboard/plans',
                'profile': '/dashboard/profile',
                'analytics': '/dashboard/google-analytics',
            },
            defaultRedirect: '/dashboard/statistics'
        };

        configCache = fallbackConfig;
        return fallbackConfig;
    }
};

/**
 * Gets redirect parameter from URL search params (case-insensitive)
 * Supports: ?redirect=, ?Redirect=, ?REDIRECT=, etc.
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {string|null} - Raw redirect value from URL or null
 */
const getRedirectParamFromURL = (searchParams) => {
    // Try all possible case variations of the parameter name
    const possibleKeys = ['redirect', 'Redirect', 'REDIRECT'];

    for (const key of possibleKeys) {
        const value = searchParams.get(key);
        if (value) {
            return value;
        }
    }

    // If exact matches don't work, iterate through all params (slower but most robust)
    for (const [key, value] of searchParams.entries()) {
        if (normalize(key) === 'redirect') {
            return value;
        }
    }

    return null;
};

/**
 * Extracts and validates redirect parameter from URL
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {Promise<string|null>} - Valid normalized redirect value or null
 */
export const getRedirectParameter = async (searchParams) => {
    const redirectValue = getRedirectParamFromURL(searchParams);

    if (!redirectValue) {
        return null;
    }

    const config = await fetchRedirectConfig();
    const normalized = normalize(redirectValue);

    if (normalized && normalized in config.redirectMap) {
        return normalized;
    }

    console.warn(`[RedirectHandler] Invalid redirect value: ${redirectValue}`);
    return null;
};

/**
 * Gets the actual URL to redirect to based on redirect parameter
 * @param {string|null} redirectValue - The redirect parameter value
 * @returns {Promise<string>} - The full redirect URL with query parameters
 */
export const getRedirectUrl = async (redirectValue) => {
    const config = await fetchRedirectConfig();
    const normalized = normalize(redirectValue);

    const basePath = normalized && config.redirectMap[normalized]
        ? config.redirectMap[normalized]
        : config.defaultRedirect;

    return `${basePath}?refreshCredits=true`;
};

/**
 * Saves redirect URL to localStorage for persistence across navigation
 * @param {string} redirectValue - The redirect parameter value
 */
export const saveRedirectUrl = async (redirectValue) => {
    const config = await fetchRedirectConfig();
    const normalized = normalize(redirectValue);

    if (normalized && normalized in config.redirectMap) {
        localStorage.setItem(REDIRECT_STORAGE_KEY, normalized);
    } else {
        console.warn(`[RedirectHandler] Cannot save invalid redirect: ${redirectValue}`);
    }
};

/**
 * Clears redirect URL from localStorage
 */
export const clearRedirectUrl = () => {
    localStorage.removeItem(REDIRECT_STORAGE_KEY);
    // console.log('[RedirectHandler] Cleared redirect');
};

/**
 * Gets stored redirect URL from localStorage
 * @returns {string|null} - Stored redirect value or null
 */
export const getStoredRedirectUrl = () => {
    const stored = localStorage.getItem(REDIRECT_STORAGE_KEY);
    // console.log(`[RedirectHandler] Retrieved stored redirect: ${stored}`);
    return stored;
};

/**
 * Main handler for post-authentication redirect
 * Reads from localStorage, determines the redirect URL, and navigates
 * @param {function} navigate - React Router navigate function
 * @param {boolean} clearAfterUse - Whether to clear stored redirect after use (default: true)
 */
export const handlePostAuthRedirect = async (navigate, clearAfterUse = true) => {
    // Prevent double execution within a single redirect — but guard against a
    // STALE flag. The flag is normally cleared 500ms after navigation; if the
    // tab reloads inside that window, a plain 'true' would persist in
    // sessionStorage and wedge the NEXT login on this tab (authenticated user
    // stuck on the login page). Store a timestamp and ignore it once it's older
    // than STALE_MS so a wedged flag self-heals.
    const STALE_MS = 5000;
    const inProgressRaw = sessionStorage.getItem(REDIRECT_IN_PROGRESS_KEY);
    const inProgressAt = inProgressRaw ? Number(inProgressRaw) : 0;
    if (inProgressAt && Number.isFinite(inProgressAt) && (Date.now() - inProgressAt) < STALE_MS) {
        // console.log('[RedirectHandler] Redirect already in progress, skipping...');
        return;
    }

    // Mark redirect as in progress (timestamped so a stale flag can expire)
    sessionStorage.setItem(REDIRECT_IN_PROGRESS_KEY, String(Date.now()));

    const redirectValue = getStoredRedirectUrl();
    const redirectUrl = await getRedirectUrl(redirectValue);

    // console.log(`[RedirectHandler] Redirecting to: ${redirectUrl}`);

    // Navigate first
    navigate(redirectUrl, { replace: true });

    // Clear both flags after a short delay to ensure navigation completes
    if (clearAfterUse) {
        setTimeout(() => {
            clearRedirectUrl();
            sessionStorage.removeItem(REDIRECT_IN_PROGRESS_KEY);
            // console.log('[RedirectHandler] Cleared redirect after navigation');
        }, 500);
    }
};

/**
 * Initialize redirect parameter from URL (call this on component mount)
 * @param {URLSearchParams} searchParams - URL search parameters
 */
export const initializeRedirect = async (searchParams) => {
    const redirectValue = await getRedirectParameter(searchParams);
    if (redirectValue) {
        await saveRedirectUrl(redirectValue);
    }
};

/**
 * Validates if a redirect destination is supported
 * @param {string} redirectValue - The redirect parameter value
 * @returns {Promise<boolean>} - True if redirect is valid
 */
export const isValidRedirect = async (redirectValue) => {
    const config = await fetchRedirectConfig();
    const normalized = normalize(redirectValue);
    return normalized && normalized in config.redirectMap;
};

/**
 * Gets all available redirect destinations (for documentation/debugging)
 * @returns {Promise<Array<string>>} - Array of valid redirect values
 */
export const getAvailableRedirects = async () => {
    const config = await fetchRedirectConfig();
    return Object.keys(config.redirectMap);
};

/**
 * Clears the config cache (useful for testing or forcing a refresh)
 */
export const clearConfigCache = () => {
    configCache = null;
    // console.log('[RedirectHandler] Cleared config cache');
};

export default {
    getRedirectParameter,
    getRedirectUrl,
    saveRedirectUrl,
    clearRedirectUrl,
    getStoredRedirectUrl,
    handlePostAuthRedirect,
    initializeRedirect,
    fetchRedirectConfig,
    clearConfigCache,
    isValidRedirect,
    getAvailableRedirects,
};